import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmButton from "@/components/ConfirmButton";

const STATUS_COLORS = {
  "Pendente": "bg-amber-100 text-amber-800",
  "Em andamento": "bg-sky-100 text-sky-800",
  "Concluído": "bg-emerald-100 text-emerald-800",
};
const RISK_COLORS = {
  "Baixo": "bg-emerald-100 text-emerald-800",
  "Médio": "bg-yellow-100 text-yellow-900",
  "Alto": "bg-red-100 text-red-800",
};
const CHART_PALETTE = ["#047857", "#0369a1", "#d97706", "#7c3aed", "#dc2626",
                      "#0891b2", "#65a30d", "#db2777"];

const EMPTY_FORM = {
  mes: "Janeiro", acao: "", responsavel: "", status: "Pendente",
  risco: "Médio", tema: "", evidencia: "",
};

export default function AnnualPlan({ companyIdOverride }) {
  const params = useParams();
  const companyId = companyIdOverride || params.companyId;
  const { user } = useAuth();
  const isTrial = Boolean(user?.is_trial);
  const [company, setCompany] = useState(null);
  const [actions, setActions] = useState([]);
  const [template, setTemplate] = useState(null);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);   // action being edited (or null)
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [filterTheme, setFilterTheme] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [c, list, s, t] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/annual-plan/${companyId}`),
        api.get(`/annual-plan/${companyId}/stats`),
        api.get(`/annual-plan/template`),
      ]);
      setCompany(c.data);
      setActions(list.data);
      setStats(s.data);
      setTemplate(t.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  }, [companyId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.acao.trim()) {
      toast.error("Informe a ação/campanha.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/annual-plan/${companyId}/${editing}`, form);
        toast.success("Ação atualizada");
      } else {
        await api.post(`/annual-plan/${companyId}`, form);
        toast.success("Ação adicionada");
      }
      setForm(EMPTY_FORM);
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      mes: a.mes, acao: a.acao, responsavel: a.responsavel || "",
      status: a.status, risco: a.risco, tema: a.tema || "",
      evidencia: a.evidencia || "",
    });
    window.scrollTo({ top: document.getElementById("annual-form").offsetTop - 80, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const remove = async (id) => {
    try {
      await api.delete(`/annual-plan/${companyId}/${id}`);
      toast.success("Ação removida");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const seed = async (force = false) => {
    try {
      const r = await api.post(`/annual-plan/${companyId}/seed${force ? "?force=true" : ""}`);
      if (r.data.skipped_because_exists) {
        if (window.confirm(`Já existem ${r.data.skipped_because_exists} ações cadastradas. Deseja somar as 12 do template Sollute mesmo assim?`)) {
          return seed(true);
        }
        return;
      }
      toast.success(`${r.data.seeded} ações importadas do template Sollute`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/annual-plan/${companyId}/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plano_anual_sst_${company?.base_slug || companyId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const triggerPrint = () => {
    if (isTrial) {
      toast.error("Geração de PDF disponível apenas no plano completo. Veja os planos para assinar agora.");
      api.post("/leads/upgrade-click", {
        event_type: "paywall_hit",
        context: `Annual plan print attempt · company ${companyId}`,
      }).catch(() => {});
      setTimeout(() => { window.location.href = "/app/planos"; }, 1500);
      return;
    }
    // A4 portrait, comfortable margins
    const styleId = "annual-plan-page-style";
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = "@page { size: A4 portrait; margin: 16mm 14mm; }";
    setTimeout(() => {
      try { window.print(); }
      catch (err) { toast.error("Falha ao abrir impressão: " + err.message); }
    }, 300);
  };

  // Calendar groups campaigns from the template, but overlays user actions on top
  const campaignsByMonth = useMemo(() => {
    if (!template) return [];
    return template.campaigns.filter((c) => {
      const q = search.toLowerCase();
      const themeOk = !filterTheme || c.grupo === filterTheme;
      const matches = !q || JSON.stringify(c).toLowerCase().includes(q);
      return themeOk && matches;
    });
  }, [template, search, filterTheme]);

  if (!template) {
    return (
      <div className="p-12 text-center text-slate-500">
        <i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…
      </div>
    );
  }

  const statusChartData = stats ? Object.entries(stats.by_status).map(([k, v]) => ({ name: k, value: v })) : [];
  const themeChartData = stats ? Object.entries(stats.by_theme).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="p-6 lg:p-12 max-w-7xl mx-auto" data-testid="annual-plan-page">
      {/* Print-only cover (only renders during print) */}
      <div className="hidden print:block mb-8 pb-6 border-b-2 border-emerald-700">
        <div className="overline text-emerald-800">Sollute Ocupacional · NR-01 / Riscos Psicossociais</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">
          Plano Anual de SST
        </h1>
        <div className="mt-3 text-sm text-slate-700 grid grid-cols-2 gap-y-1">
          <div><strong>Empresa:</strong> {company?.razao_social || "—"}</div>
          <div><strong>CNPJ:</strong> {company?.cnpj || "—"}</div>
          <div><strong>Gerado em:</strong> {new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</div>
          <div><strong>Responsável:</strong> {user?.name || user?.email || "—"}</div>
        </div>
      </div>

      {/* Header (screen) */}
      <div className="flex flex-wrap justify-between items-end gap-4 mb-10 print:hidden">
        <div>
          <Link to="/app/empresas" className="overline text-emerald-700 hover:underline">
            <i className="fa-solid fa-arrow-left mr-1"></i> Empresas
          </Link>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 mt-2">
            Plano Anual SST
          </h1>
          <p className="text-slate-600 mt-1">
            {company?.razao_social || "—"} ·{" "}
            <span className="text-slate-500">Campanhas, DDS, inspeções e treinamentos do ano</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => seed()} className="btn btn-ghost border border-emerald-300 text-emerald-900 hover:bg-emerald-50"
                  data-testid="annual-plan-seed-button">
            <i className="fa-solid fa-sparkles"></i> Importar template Sollute
          </button>
          <button onClick={downloadCsv} className="btn btn-secondary"
                  data-testid="annual-plan-export-button">
            <i className="fa-solid fa-file-csv"></i> Exportar CSV
          </button>
          <button onClick={triggerPrint} className="btn btn-primary"
                  data-testid="annual-plan-print-button"
                  title={isTrial ? "Disponível apenas no plano completo" : "Gerar PDF do plano anual"}>
            <i className="fa-solid fa-file-pdf"></i> Imprimir / PDF
          </button>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="annual-plan-kpis">
          <Kpi label="Ações no ano" value={stats.total} hint="Campanhas, DDS, inspeções" testid="kpi-total" />
          <Kpi label="Concluídas" value={`${stats.done_percent}%`} hint={`${stats.done} de ${stats.total}`} accent="emerald" testid="kpi-done" />
          <Kpi label="Saúde Mental" value={stats.psico} hint="Ligadas à NR-01" accent="sky" testid="kpi-psico" />
          <Kpi label="Prioridade Alta" value={stats.alta} hint="Temas críticos" accent="rose" testid="kpi-alta" />
        </div>
      )}

      {/* Charts */}
      {stats && stats.total > 0 && (
        <div className="grid lg:grid-cols-3 gap-4 mb-8">
          <div className="card lg:col-span-2">
            <div className="overline mb-1">Dashboard de Execução</div>
            <h2 className="font-semibold text-lg text-slate-900 mb-4">Status das ações</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#047857" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card">
            <div className="overline mb-1">Distribuição</div>
            <h2 className="font-semibold text-lg text-slate-900 mb-4">Por Tema</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={themeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={false}>
                  {themeChartData.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="card mb-8">
        <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
          <div>
            <div className="overline">Calendário Anual</div>
            <h2 className="font-semibold text-lg text-slate-900 mt-1">Campanhas mensais sugeridas</h2>
          </div>
          <div className="flex gap-2 flex-wrap print:hidden">
            <input type="search" placeholder="Buscar mês, tema ou ação..."
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   className="form-input text-sm" data-testid="annual-plan-search" />
            <select value={filterTheme} onChange={(e) => setFilterTheme(e.target.value)}
                    className="form-input text-sm" data-testid="annual-plan-theme-filter">
              <option value="">Todos os temas</option>
              {template.themes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaignsByMonth.map((c) => (
            <div key={c.mes} className="border border-slate-200 bg-gradient-to-br from-white to-emerald-50/30 p-4 avoid-break">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg font-bold text-slate-900">{c.mes}</h3>
                <span className={`text-xs font-bold px-2 py-1 ${RISK_COLORS[c.risco]}`}>{c.risco}</span>
              </div>
              <div className="text-sm font-semibold text-emerald-800 mb-2">{c.tema}</div>
              <ul className="text-xs text-slate-700 space-y-1 mb-3 list-disc list-inside">
                {c.acoes.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
              <div className="flex flex-wrap gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-800">{c.grupo}</span>
                {c.indicadores.map((i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-700">{i}</span>
                ))}
              </div>
            </div>
          ))}
          {campaignsByMonth.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-8">
              Nenhuma campanha encontrada com esses filtros.
            </div>
          )}
        </div>
      </div>

      {/* Action Form */}
      <div id="annual-form" className="card mb-8 print:hidden">
        <div className="overline">{editing ? "Editar ação" : "Nova ação"}</div>
        <h2 className="font-semibold text-lg text-slate-900 mb-4 mt-1">
          {editing ? "Atualizar ação existente" : "Plano de Ação Gerencial"}
        </h2>
        <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input className="form-input" placeholder="Ação / Campanha"
                 value={form.acao} onChange={(e) => setForm({ ...form, acao: e.target.value })}
                 required data-testid="annual-form-action" />
          <input className="form-input" placeholder="Responsável"
                 value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                 data-testid="annual-form-responsible" />
          <select className="form-input" value={form.mes}
                  onChange={(e) => setForm({ ...form, mes: e.target.value })}
                  data-testid="annual-form-month">
            {template.months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="form-input" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="annual-form-status">
            {template.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-input" value={form.risco}
                  onChange={(e) => setForm({ ...form, risco: e.target.value })}
                  data-testid="annual-form-risk">
            {template.risks.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="form-input" value={form.tema}
                  onChange={(e) => setForm({ ...form, tema: e.target.value })}
                  data-testid="annual-form-theme">
            <option value="">Tema (opcional)</option>
            {template.themes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <textarea className="form-input sm:col-span-2 lg:col-span-3"
                    placeholder="Evidência: lista de presença, fotos, ata, DDS, relatório, certificado..."
                    rows={2} value={form.evidencia}
                    onChange={(e) => setForm({ ...form, evidencia: e.target.value })}
                    data-testid="annual-form-evidence" />
          <div className="sm:col-span-2 lg:col-span-3 flex gap-2 justify-end">
            {editing && (
              <button type="button" onClick={cancelEdit} className="btn btn-ghost"
                      data-testid="annual-form-cancel">Cancelar</button>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy}
                    data-testid="annual-form-submit">
              <i className={`fa-solid ${editing ? "fa-check" : "fa-plus"}`}></i>
              {" "}{editing ? "Salvar alterações" : "Adicionar ação"}
            </button>
          </div>
        </form>
      </div>

      {/* Action Table */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg text-slate-900">
            Ações cadastradas ({actions.length})
          </h2>
        </div>
        {actions.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <i className="fa-solid fa-clipboard-list text-4xl text-slate-300 mb-3"></i>
            <p>Nenhuma ação cadastrada ainda.</p>
            <p className="text-xs mt-1">Clique em <strong>Importar template Sollute</strong> ou cadastre acima.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="annual-plan-table">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-3 font-semibold uppercase text-xs tracking-wider">Mês</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Ação</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Responsável</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Status</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Risco</th>
                  <th className="font-semibold uppercase text-xs tracking-wider">Tema</th>
                  <th className="print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`annual-row-${a.id}`}>
                    <td className="py-3 font-semibold text-slate-900">{a.mes}</td>
                    <td className="text-slate-700">{a.acao}</td>
                    <td className="text-slate-600">{a.responsavel || "—"}</td>
                    <td>
                      <span className={`text-xs font-bold px-2 py-1 ${STATUS_COLORS[a.status]}`}>{a.status}</span>
                    </td>
                    <td>
                      <span className={`text-xs font-bold px-2 py-1 ${RISK_COLORS[a.risco]}`}>{a.risco}</span>
                    </td>
                    <td className="text-slate-600 text-xs">{a.tema || "—"}</td>
                    <td className="text-right whitespace-nowrap print:hidden">
                      <button onClick={() => startEdit(a)} className="btn btn-ghost text-xs"
                              data-testid={`annual-edit-${a.id}`}>
                        <i className="fa-solid fa-pen"></i>
                      </button>
                      <ConfirmButton onConfirm={() => remove(a.id)} testid={`annual-delete-${a.id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legal Routine Reference */}
      <div className="card mb-8">
        <div className="overline">Referência</div>
        <h2 className="font-semibold text-lg text-slate-900 mb-1 mt-1">Rotina Legal Complementar</h2>
        <p className="text-sm text-slate-500 mb-4">
          Itens permanentes que devem compor o sistema de gestão da empresa.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 font-semibold uppercase text-xs tracking-wider">Periodicidade</th>
                <th className="font-semibold uppercase text-xs tracking-wider">Ação</th>
                <th className="font-semibold uppercase text-xs tracking-wider">Evidência recomendada</th>
                <th className="font-semibold uppercase text-xs tracking-wider">Integração</th>
              </tr>
            </thead>
            <tbody>
              {template.legal_routines.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 font-semibold text-emerald-800">{r.periodicidade}</td>
                  <td className="text-slate-700">{r.acao}</td>
                  <td className="text-slate-600 text-xs">{r.evidencia}</td>
                  <td className="text-slate-600 text-xs font-mono">{r.integracao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 p-4 bg-emerald-50 border-l-4 border-emerald-600 text-sm text-emerald-900">
          <strong>Orientação técnica:</strong> este calendário deve ser adaptado ao grau de risco,
          CNAE, atividades reais, histórico de acidentes, resultados do PCMSO,
          inventário de riscos e diagnóstico psicossocial de cada empresa.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, accent, testid }) {
  const colorClass = {
    emerald: "text-emerald-700",
    sky: "text-sky-700",
    rose: "text-rose-700",
  }[accent] || "text-slate-900";
  return (
    <div className="card" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-display text-3xl font-extrabold ${colorClass} mt-1`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{hint}</div>
    </div>
  );
}
